import { create } from 'zustand';
import {
  ACCENT_KEY,
  DEFAULT_ACCENT,
  DEFAULT_FONT,
  DEFAULT_TEXT_SIZE,
  FONT_KEY,
  TEXT_SIZE_KEY,
  applyAccent,
  applyFont,
  applyTextSize,
} from '@/lib/appearance';

export interface Toast {
  id: string;
  title: string;
  description?: string;
  tone: 'success' | 'error' | 'info';
  actionLabel?: string;
  onClick?: () => void;
}

interface UiState {
  theme: 'light' | 'dark';
  /** Which accent family carries the app's colour. */
  accent: string;
  /** Which typeface the app is set in. */
  font: string;
  /** The root font size every `rem` is measured from. */
  textSize: string;
  language: 'en' | 'ar';
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;
  commandPaletteOpen: boolean;
  notificationsOpen: boolean;
  chatOpen: boolean;
  chatChannelId?: string;
  toasts: Toast[];

  toggleSidebar: () => void;
  setSidebar: (open: boolean) => void;
  toggleSidebarCollapsed: () => void;
  toggleTheme: () => void;
  setTheme: (theme: 'light' | 'dark') => void;
  setAccent: (accent: string) => void;
  setFont: (font: string) => void;
  setTextSize: (size: string) => void;
  toggleLanguage: () => void;
  setCommandPalette: (open: boolean) => void;
  setNotifications: (open: boolean) => void;
  setChat: (open: boolean, channelId?: string) => void;
  toast: (input: Omit<Toast, 'id'> | string) => void;
  dismissToast: (id: string) => void;
}

export const useUi = create<UiState>((set, get) => ({
  theme: document.documentElement.classList.contains('light') ? 'light' : 'dark',
  accent: localStorage.getItem(ACCENT_KEY) ?? DEFAULT_ACCENT,
  font: localStorage.getItem(FONT_KEY) ?? DEFAULT_FONT,
  textSize: localStorage.getItem(TEXT_SIZE_KEY) ?? DEFAULT_TEXT_SIZE,
  language: document.documentElement.lang === 'ar' ? 'ar' : 'en',
  /* A persistent rail on desktop, a drawer that starts closed below `lg`. */
  sidebarOpen: window.matchMedia('(min-width: 1024px)').matches,
  sidebarCollapsed: localStorage.getItem('itsappy.sidebarCollapsed') === 'true',
  commandPaletteOpen: false,
  notificationsOpen: false,
  chatOpen: false,
  chatChannelId: undefined,
  toasts: [],

  toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),
  setSidebar: (sidebarOpen) => set({ sidebarOpen }),
  toggleSidebarCollapsed: () => set((state) => {
    const sidebarCollapsed = !state.sidebarCollapsed;
    localStorage.setItem('itsappy.sidebarCollapsed', String(sidebarCollapsed));
    return { sidebarCollapsed };
  }),
  toggleTheme: () => get().setTheme(get().theme === 'dark' ? 'light' : 'dark'),

  setTheme: (theme) => set(() => {
    document.documentElement.classList.toggle('light', theme === 'light');
    document.documentElement.classList.toggle('dark', theme === 'dark');
    localStorage.setItem('itsappy.theme', theme);
    document.querySelector('meta[name="theme-color"]')?.setAttribute(
      'content',
      theme === 'light' ? '#ffffff' : '#0b1120',
    );
    return { theme };
  }),

  setAccent: (accent) => {
    applyAccent(accent);
    localStorage.setItem(ACCENT_KEY, accent);
    set({ accent });
  },

  setFont: (font) => {
    applyFont(font);
    localStorage.setItem(FONT_KEY, font);
    set({ font });
  },

  setTextSize: (textSize) => {
    applyTextSize(textSize);
    localStorage.setItem(TEXT_SIZE_KEY, textSize);
    set({ textSize });
  },
  toggleLanguage: () => set((state) => {
    const language = state.language === 'en' ? 'ar' : 'en';
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    localStorage.setItem('itsappy.language', language);
    return { language };
  }),
  setCommandPalette: (commandPaletteOpen) => set({ commandPaletteOpen }),
  setNotifications: (notificationsOpen) => set({ notificationsOpen }),
  setChat: (chatOpen, chatChannelId) => set({ chatOpen, chatChannelId }),

  toast(input) {
    const toast: Toast =
      typeof input === 'string'
        ? { id: crypto.randomUUID(), title: input, tone: 'info' }
        : { id: crypto.randomUUID(), ...input };
    set((s) => ({ toasts: [...s.toasts, toast] }));
    const duration = toast.onClick ? 7000 : toast.tone === 'error' ? 6000 : 3500;
    setTimeout(() => get().dismissToast(toast.id), duration);
  },

  dismissToast(id) {
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
  },
}));

export const toast = {
  success: (title: string, description?: string) =>
    useUi.getState().toast({ title, description, tone: 'success' }),
  error: (title: string, description?: string) =>
    useUi.getState().toast({ title, description, tone: 'error' }),
  info: (
    title: string,
    description?: string,
    action?: { label: string; onClick: () => void },
  ) =>
    useUi.getState().toast({
      title,
      description,
      tone: 'info',
      actionLabel: action?.label,
      onClick: action?.onClick,
    }),
};
